import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FC,
} from "react";
import { useSearchParams } from "react-router-dom";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

import checkAnimation from "@/shared/assets/animations/check.lottie?url";
import papersIcon from "@/shared/assets/icons/papers-icon.svg";
import { Button } from "@/shared/components/ui/button";
import { FileCard } from "@/shared/components/ui/file-card";
import { convertImageFileToPdf } from "@/shared/utils/convertImageFileToPdf";
import { supabase } from "@/shared/lib/supabase";

import type { FileStatus, UploadedFile } from "@/shared/types/uploadTypes";
import { sentryUtils } from "@/shared/utils/sentry";
import { DOCUMENT_LIMIT_MESSAGE } from "@/shared/utils/uploadDocument";

export const MobileUploadPage: FC = () => {
  const [params] = useSearchParams();
  const sid = params.get("sid") || "";
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [accessToken, setAccessToken] = useState<string>("");
  const [remainingUploads, setRemainingUploads] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [limitError, setLimitError] = useState<string | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!sid) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const { data, error } = await supabase
          .from("upload_sessions")
          .select("access_token, status, expires_at, remaining_uploads")
          .eq("session_id", Number(sid))
          .single();

        if (error || !data || data.status !== "active" || new Date(data.expires_at) < new Date()) {
          setLoading(false);
          return;
        }

        setAccessToken(data.access_token || "");
        setRemainingUploads(data.remaining_uploads ?? null);
        setLoading(false);
      } catch (err) {
        sentryUtils.captureError(err, "Mobile upload session load failed");
        setLoading(false);
      }
    })();
  }, [sid]);

  const canUpload = useMemo(() => Boolean(sid && accessToken), [sid, accessToken]);

  useEffect(() => {
    if (!sid) return;
    channelRef.current = supabase.channel(`mobile_upload:${sid}`);
    channelRef.current.subscribe();
    return () => {
      channelRef.current?.unsubscribe();
    };
  }, [sid]);

  const handleSelect = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length > 0) {
      setUploadedFiles((prev) => [
        ...prev,
        ...selected.map((file) => ({ file, status: "idle" as FileStatus })),
      ]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSendToComputer = useCallback(async () => {
    if (!canUpload || uploadedFiles.length === 0) return;
    setUploading(true);
    try {
      let hitLimit = false;
      for (let i = 0; i < uploadedFiles.length; i++) {
        const { file } = uploadedFiles[i];
        setUploadedFiles((prev) =>
          prev.map((uf, idx) => (idx === i ? { ...uf, status: "loading" as FileStatus } : uf)),
        );

        let toUpload = file;
        let finalName = file.name;
        if (file.type.startsWith("image/")) {
          const converted = await convertImageFileToPdf(file);
          toUpload = converted;
          finalName = converted.name;
        }

        const presignedResponse = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/mobile/presigned-url`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ fileName: finalName, sessionId: sid }),
          },
        );
        if (!presignedResponse.ok) {
          if (presignedResponse.status === 403 || presignedResponse.status === 429) {
            hitLimit = true;
            const uploaded = i;
            const blocked = uploadedFiles.length - i;
            setLimitError(
              uploaded > 0
                ? `${uploaded} document${uploaded === 1 ? "" : "s"} uploaded. ${blocked} could not be uploaded — you've reached your free plan limit.`
                : DOCUMENT_LIMIT_MESSAGE,
            );
            setUploadedFiles([]);
            break;
          }
          throw new Error(`Failed presign: ${presignedResponse.status}`);
        }
        const presignedData = await presignedResponse.json();
        const { uploadUrl, fileKey } = presignedData;
        if (presignedData.remaining_uploads != null) {
          setRemainingUploads(presignedData.remaining_uploads);
        }

        const put = await fetch(uploadUrl, {
          method: "PUT",
          body: toUpload,
          headers: { "Content-Type": toUpload.type || "application/octet-stream" },
        });
        if (!put.ok) {
          const errorText = await put.text();
          throw new Error(`Upload failed: ${put.status} ${errorText}`);
        }

        await channelRef.current?.send({
          type: "broadcast",
          event: "file_uploaded",
          payload: {
            fileKey,
            fileName: finalName,
            contentType: toUpload.type,
            uploadedAt: new Date().toISOString(),
          },
        });

        setUploadedFiles((prev) =>
          prev.map((uf, idx) => (idx === i ? { ...uf, status: "complete" as FileStatus } : uf)),
        );
      }

      if (!hitLimit) {
        setSuccess(true);
        setUploadedFiles([]);
      }
    } catch (e) {
      alert((e as Error).message || "Upload failed");
      sentryUtils.captureError(e, "Mobile upload failed");
    } finally {
      setUploading(false);
    }
  }, [canUpload, uploadedFiles, accessToken]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-subtle p-6">
        <div className="w-full max-w-md rounded-lg border border-primary bg-white p-6">
          <p className="text-center text-secondary text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!sid || !accessToken) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-subtle p-6">
        <div className="w-full max-w-md rounded-lg border border-primary bg-white p-6">
          <h3 className="text-center mb-2">Invalid or expired upload link</h3>
          <p className="text-center text-secondary">Please rescan the QR code from your desktop.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-subtle p-6">
        <div className="w-full max-w-md rounded-lg border border-primary bg-white p-6 text-center">
          <div className="mx-auto mb-3 h-16 w-16">
            <DotLottieReact src={checkAnimation} loop={false} autoplay />
          </div>
          <h3 className="mb-1">All set!</h3>
          <p className="text-pretty text-secondary">
            Your documents are now in your Elayne account. You can close this tab.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-subtle">
      <div className="sticky top-0 z-10 border-b border-primary bg-white px-4 py-4">
        <h3 className="text-center">Add Documents</h3>
        <p className="mt-1 text-center text-secondary">
          Take a photo or choose files from your phone
        </p>
        {remainingUploads != null && (
          <p className="mt-1 text-center text-2xs text-tertiary">
            {remainingUploads} {remainingUploads === 1 ? "upload" : "uploads"} remaining
          </p>
        )}
      </div>

      <div className="flex-1 p-4 pb-24">
        {limitError && (
          <div className="mb-4 rounded-lg border border-blue-500 bg-blue-50 p-3 text-center">
            <p className="text-sm">{limitError}</p>
          </div>
        )}
        <label
          htmlFor="file-input"
          className="flex h-[260px] w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-primary bg-white transition-colors hover:bg-subtle/50"
        >
          <img src={papersIcon} alt="Upload documents" className="mb-3 h-14 w-14" />
          <p className="font-medium">
            {uploadedFiles.length === 0 ? "Tap to add" : "Tap to add more"}
          </p>
          <p className="text-tertiary">PDF, PNG, JPG, or HEIC</p>
        </label>
        <input
          ref={fileInputRef}
          id="file-input"
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,image/*"
          onChange={handleSelect}
          className="hidden"
        />

        {uploadedFiles.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="px-1 text-sm font-medium text-secondary">
              Documents ({uploadedFiles.length})
            </p>
            {uploadedFiles.map((uploadedFile, idx) => (
              <FileCard
                key={`${uploadedFile.file.name}-${idx}`}
                fileName={uploadedFile.file.name}
                fileSize={uploadedFile.file.size}
                status={uploadedFile.status}
                onRemove={() => handleRemoveFile(idx)}
                isRemoving={uploading}
              />
            ))}
          </div>
        )}
      </div>

      {uploadedFiles.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-primary bg-white p-4">
          <Button
            onClick={handleSendToComputer}
            disabled={uploading}
            variant="brand"
            className="w-full"
            size="lg"
          >
            {uploading
              ? "Sending..."
              : `Send ${uploadedFiles.length} document${uploadedFiles.length > 1 ? "s" : ""} to computer`}
          </Button>
        </div>
      )}
    </div>
  );
};
